import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants, loadAgreementsInto } from './ptlSetupApi'
import { ROUTES, num } from './constants'
import { agreementLines, agreementStatus, areaStatus, readiness, summaryKpis } from './components/MerchantStatus'
import { Banner, Breadcrumb, changeText } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import AddPickupDrawer from './components/AddPickupDrawer'
import AgreementDialog from './components/AgreementDialog'

const list = (xs, max = 4) => (xs.length > max ? `${xs.slice(0, max).join(', ')} and ${xs.length - max} more` : xs.join(', '))

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant Update → one merchant (plan screen 12). What is set for this live PTL
 * merchant, one card per area; each card opens that area's screen: Pricing, TAT and Allocation as the onboarding steps
 * in update mode, Zone map as its own screen. The merchant details page holds the merchant's profile.
 */
export default function PtlMerchantHub() {
  const { merchantId } = useParams()
  const navigate = useNavigate()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [addingPickup, setAddingPickup] = useState(false)
  const [agreementOpen, setAgreementOpen] = useState(false)

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then((ms) => { setMerchants(ms); loadAgreementsInto(setMerchants) }).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const m = useMemo(() => merchants?.find((x) => x.id === merchantId), [merchants, merchantId])
  const crumb = <Breadcrumb parent='Merchant Update' onParent={() => navigate(ROUTES.update)} current={m?.name || 'Merchant'} />

  if (merchants && !m) {
    return (
      <PtlPage title='Merchant Update' breadcrumb={crumb}>
        <EmptyState title='Not a live PTL merchant'
          body='This merchant is not in the PTL merchant list. Use Onboard a merchant on Onboarding Health to turn on PTL for it, or pick another merchant.'
          action={<Button variant='contained' onClick={() => navigate(ROUTES.update)} sx={SX.cta}>Back to Merchant Update</Button>} />
      </PtlPage>
    )
  }

  const s = m ? areaStatus(m) : null
  const r = m ? readiness(m) : null
  const ag = m?.agreement
  const cards = m ? [
    {
      key: 'pricing', title: 'Pricing', status: s.pricing, to: ROUTES.updateStep(m.id, 'pricing'), action: 'Open pricing',
      lines: [m.rateCardCouriers.length ? `Rate cards for ${list(m.rateCardCouriers)}.` : 'No PTL rate card: PTL orders get no price.',
        'Upload or replace a courier’s card, and see its lane coverage.'],
    },
    {
      key: 'tat', title: 'TAT', status: s.tat, to: ROUTES.updateStep(m.id, 'tat'), action: 'Open TAT',
      second: { label: 'Effective TAT matrix', to: `${ROUTES.tatCustom}/${m.id}` },
      lines: [m.customTatLanes ? `Custom TAT on ${num(m.customTatLanes)} lane${m.customTatLanes === 1 ? '' : 's'}, Prozo standard on the rest.` : 'Prozo standard TAT on every lane.',
        m.tatLatest ? `Last change ${changeText(m.tatLatest)}.` : 'Set a custom TAT only where the agreement gives its own.'],
    },
    {
      key: 'allocation', title: 'Allocation', status: s.allocation, to: ROUTES.updateStep(m.id, 'allocation'), action: 'Open allocation',
      lines: [m.allocationCouriers.length ? `Allocated to ${list(m.allocationCouriers)}.` : 'No couriers picked: every PTL pickup goes to the cheapest courier with a rate card.',
        'Only couriers named in the commercial contract.'],
    },
    {
      key: 'zone', title: 'Zone map', status: s.zone, to: `${ROUTES.zoneOverrides}/${m.id}`, action: 'Open zone map',
      lines: [m.ownZoneMapPincodes ? `${num(m.ownZoneMapPincodes)} pincode${m.ownZoneMapPincodes === 1 ? '' : 's'} use this merchant’s own zone; the rest the standard map.` : 'Standard zone map for every pincode.',
        m.zoneMapLatest ? `Last change ${changeText(m.zoneMapLatest)}.` : 'Override only if the contract maps pincodes differently.'],
    },
    {
      key: 'kyc', title: 'KYC', status: r.kyc, to: ROUTES.merchantKyc(m.id), action: 'Open KYC in merchant details',
      lines: [r.kyc.ok ? 'KYC is approved.' : 'PTL orders are refused until KYC is approved.',
        'Documents are uploaded and approved on the merchant details page, Basic Details tab.'],
    },
    {
      key: 'agreement', title: 'Agreement', status: agreementStatus(ag), onClick: () => setAgreementOpen(true), action: 'Open agreement',
      lines: agreementLines(ag),
    },
    {
      key: 'pickup', title: 'Pickup locations', status: r.pickup, onClick: () => setAddingPickup(true), action: 'Add pickup location',
      second: { label: 'Manage in Pickup Locations', to: ROUTES.pickupLocations },
      lines: [r.pickup.ok ? `${num(m.pickupLocations)} pickup location${m.pickupLocations === 1 ? '' : 's'} to book from.` : 'No pickup location: an order needs the warehouse it is picked up from.',
        'Edit, set the default or deactivate them in Merchant Setup → Pickup Locations.'],
    },
  ] : []

  return (
    <PtlPage title={m ? m.name : 'Merchant Update'} breadcrumb={crumb}
      description='What is set up for this PTL merchant. Each area opens its own screen; every change there is versioned and can be rolled back.'
      actions={m && (
        <Button variant='outlined' startIcon={<OpenInNewIcon />} component={RouterLink} to={`/merchant-config/merchant/${m.id}`} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
          Open merchant details
        </Button>
      )}
      kpis={summaryKpis(m)} kpisLoading={!merchants && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {m && (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {cards.map((c) => (
            <Card key={c.key} elevation={0} sx={{ ...SX.card, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component='h2' sx={{ ...TYPE.sectionTitle, flex: 1 }}>{c.title}</Typography>
                <ReadinessChip tone={c.status.tone} label={c.status.label} />
              </Box>
              {c.lines.map((l) => <Typography key={l} sx={{ ...TYPE.rowHelp, color: C.inkBody }}>{l}</Typography>)}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto', pt: 0.5 }}>
                {c.onClick
                  ? <Button variant='contained' onClick={c.onClick} sx={{ ...SX.cta, minHeight: 36 }}>{c.action}</Button>
                  : <Button variant='contained' endIcon={<ChevronRightIcon />} component={RouterLink} to={c.to} sx={{ ...SX.cta, minHeight: 36 }}>{c.action}</Button>}
                {c.second && <Button component={RouterLink} to={c.second.to} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36 }}>{c.second.label}</Button>}
              </Box>
            </Card>
          ))}
        </Box>
      )}
      {m && <AddPickupDrawer merchant={m} open={addingPickup} onClose={() => setAddingPickup(false)} onSaved={load} />}
      {m && <AgreementDialog merchant={m} open={agreementOpen} onClose={() => setAgreementOpen(false)} onChanged={load} />}
    </PtlPage>
  )
}
